import { useEffect } from "react";
import { api } from "./api";

/** Track C Layer 1: web-tab context ping.
 *
 *  While the SPA is open and the tab is visible, POST a context ping
 *  every 5 min. If the user has granted geolocation we include
 *  lat/lng; otherwise we send a time-only ping (still useful for
 *  time-based requirements). We never PROMPT for geolocation here:
 *  the permission is granted explicitly on /settings/context. We only
 *  read the position if a prior grant exists (permissions API check),
 *  so opening the app never throws a browser permission dialog.
 *
 *  v1 cap: a web tab cannot capture location in the background. When
 *  the tab is hidden or closed, pings stop; the backend fills the gap
 *  with `source='inferred'` rows from the last-known location. True
 *  continuous background location needs the Track D mobile app. */
export function useContextPing(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function readPosition(): Promise<GeolocationPosition | null> {
      if (typeof navigator === "undefined" || !navigator.geolocation) return null;
      try {
        // Only read if already granted; never trigger the prompt here.
        if (navigator.permissions) {
          const st = await navigator.permissions.query({ name: "geolocation" as PermissionName });
          if (st.state !== "granted") return null;
        }
      } catch {
        return null;
      }
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos),
          () => resolve(null),
          { maximumAge: 5 * 60_000, timeout: 8_000, enableHighAccuracy: false },
        );
      });
    }

    async function ping() {
      if (cancelled || document.visibilityState === "hidden") return;
      const pos = await readPosition();
      try {
        await api.contextCapture({
          source: "web-tab-ping",
          location_lat: pos?.coords.latitude ?? null,
          location_lng: pos?.coords.longitude ?? null,
          location_accuracy: pos?.coords.accuracy ?? null,
          device_state: "web",
          local_hour: new Date().getHours(),
        });
      } catch {
        /* best-effort; a missed ping is fine, backend infers */
      }
    }

    void ping();
    const id = window.setInterval(ping, 5 * 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible") void ping();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled]);
}
